"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.setAffectFieldAttr2Action = exports.clearOldBrotherField = exports.createNewBrotherField = exports.createNewField = exports.setField = exports.createConvertActions = void 0;
const i18n_1 = require("../../exports/i18n");
const lodash_1 = require("lodash");
const model_1 = require("../../model");
const store_1 = require("../../exports/store");
const constants_1 = require("../../modules/shared/store/constants");
const selectors_1 = require("../../exports/store/selectors");
const types_1 = require("../../types");
const utils_1 = require("../../utils");
const kanban_view_1 = require("../../model/views/kanban_view");
// The store cannot be called here! !
/**
  * When the column type does not change, the single-selection and multi-selection types need to clean the cell values
  */
function changeFieldSetting(snapshot, oldField, newField) {
    const actions = [];
    if (newField.type !== oldField.type) {
        return actions;
    }
    switch (oldField.type) {
        case types_1.FieldType.MultiSelect:
        case types_1.FieldType.SingleSelect: {
            // delete records without options
            const optionIdMap = (0, lodash_1.keyBy)(newField.property.options, 'id');
            for (const recordId in snapshot.recordMap) {
                const cellValue = snapshot.recordMap[recordId].data[newField.id];
                let convertValue = cellValue;
                if (Array.isArray(cellValue)) {
                    convertValue = cellValue.filter(cv => optionIdMap[cv]);
                }
                else if (cellValue && (!optionIdMap || !optionIdMap[cellValue])) {
                    convertValue = null;
                }
                if (!(0, lodash_1.isEqual)(cellValue, convertValue)) {
                    const action = model_1.DatasheetActions.setRecord2Action(snapshot, {
                        recordId,
                        fieldId: newField.id,
                        // TODO what if convertValue is null ? value does not accept null
                        value: convertValue,
                    });
                    action && actions.push(action);
                }
            }
            return actions;
        }
        case types_1.FieldType.Link: {
            // When switching the associated datasheetId, you need to clear the value of the cell
            if (oldField.property.foreignDatasheetId !== newField.property.foreignDatasheetId) {
                for (const recordId in snapshot.recordMap) {
                    const cellValue = snapshot.recordMap[recordId].data[newField.id];
                    if (cellValue) {
                        const action = model_1.DatasheetActions.setRecord2Action(snapshot, {
                            recordId,
                            fieldId: newField.id,
                            value: null,
                        });
                        action && actions.push(action);
                    }
                }
            }
            return actions;
        }
        // Score type row score cannot be greater than the maximum value
        case types_1.FieldType.Rating: {
            if (newField.property.max < oldField.property.max) {
                for (const recordId in snapshot.recordMap) {
                    const cellValue = snapshot.recordMap[recordId].data[newField.id];
                    if (cellValue && cellValue > newField.property.max) {
                        const action = model_1.DatasheetActions.setRecord2Action(snapshot, {
                            recordId,
                            fieldId: newField.id,
                            value: newField.property.max,
                        });
                        action && actions.push(action);
                    }
                }
            }
        }
    }
    return actions;
}
/**
 * Execute general data conversion logic when the type of the column changes
 */
function switchFieldRecordData(context, snapshot, oldField, newField) {
    const { model: state, ldcMaintainer } = context;
    const actions = [];
    // Converted into an associated field to synchronize the associated data of the associated table
    // Only related fields with sibling fields need data consistency maintenance
    const needCreateLinkData = newField.type === types_1.FieldType.Link && newField.property.brotherFieldId;
    // Convert between computed fields without doing anything
    if (model_1.Field.bindContext(oldField, state).isComputed && model_1.Field.bindContext(newField, state).isComputed) {
        return {
            actions,
        };
    }
    for (const recordId in snapshot.recordMap) {
        const cellValue = store_1.Selectors.getCellValue(state, snapshot, recordId, newField.id);
        function setValue(convertValue) {
            const action = model_1.DatasheetActions.setRecord2Action(snapshot, {
                recordId,
                fieldId: newField.id,
                value: convertValue,
            });
            action && actions.push(action);
            if (needCreateLinkData) {
                const linkedSnapshot = (0, selectors_1.getSnapshot)(state, newField.property.foreignDatasheetId);
                ldcMaintainer && ldcMaintainer.insert(state, linkedSnapshot, recordId, newField, convertValue, null);
            }
        }
        if (cellValue != null) {
            // The new field is a calculated field, and the old field is an entity field, then all the original values are cleared
            if (model_1.Field.bindContext(newField, state).isComputed) {
                setValue(null);
            }
            else {
                const stdVal = model_1.Field.bindContext(oldField, state).cellValueToStdValue(cellValue);
                let convertValue = model_1.Field.bindContext(newField, state).stdValueToCellValue(stdVal);
                convertValue = (0, model_1.handleEmptyCellValue)(convertValue, model_1.Field.bindContext(newField, state).basicValueType);
                // Otherwise convert the value of the original cell
                setValue(convertValue);
            }
        }
        // delete the alarm when modifying the date column type
        if (oldField.type === types_1.FieldType.DateTime) {
            const alarm = store_1.Selectors.getDateTimeCellAlarm(snapshot, recordId, oldField.id);
            if (alarm) {
                const alarmActions = model_1.DatasheetActions.setDateTimeCellAlarm(snapshot, {
                    recordId,
                    fieldId: oldField.id,
                    alarm: null,
                });
                if (alarmActions) {
                    actions.push(...alarmActions);
                }
            }
        }
    }
    return {
        actions,
    };
}
/**
 * When the field changes, the related properties on the view should be cleared synchronously.
 */
function clearViewAttribute(snapshot, oldField, newField) {
    const actions = [];
    snapshot.meta.views.forEach(view => {
        // filter condition clear
        if (newField.type !== oldField.type && view.filterInfo) {
            const filterInfo = view.filterInfo;
            const newConditions = filterInfo.conditions.filter(condition => {
                // 1. The fieldId is not the same, indicating that there is no corresponding relationship, and no need to delete
                if (condition.fieldId !== newField.id) {
                    return true;
                }
                // 2. The fieldId is the same, but the fieldType is different and needs to be deleted
                // In theory, oldField.type is equal to condition.fieldType
                if (newField.type !== oldField.type ||
                    newField.type !== condition.fieldType) {
                    return false;
                }
                // 3. If the fieldType is not changed, and it is a single/multiple selection type, and the condition has selection options
                if (newField.type === oldField.type &&
                    newField.type === condition.fieldType &&
                    (0, utils_1.isSelectField)(newField) &&
                    (model_1.Field.isFilterBelongFieldType(types_1.FieldType.SingleSelect, condition) ||
                        model_1.Field.isFilterBelongFieldType(types_1.FieldType.MultiSelect, condition))) {
                    if (condition.value == null) {
                        return false;
                    }
                    /*
                     * If condition has an option,
                     * And the selection drop-down box does not exist this option,
                     * Then you need to delete this condition
                     */
                    const selectField = newField;
                    return condition.value.some(optionId => {
                        return selectField.property.options.every(option => {
                            return optionId !== option.id;
                        });
                    });
                }
                return true;
            });
            const action = model_1.DatasheetActions.setFilterInfo2Action(snapshot, {
                viewId: view.id,
                filterInfo: {
                    conjunction: filterInfo.conjunction,
                    conditions: newConditions,
                },
            });
            action && actions.push(action);
        }
        // Statistics type clear
        if (view.type === store_1.ViewType.Grid && newField.type !== oldField.type && !view.lockInfo) {
            let statType;
            // Convert to numeric type, automatically set it to StatType.Sum
            if (newField.type === types_1.FieldType.Number) {
                statType = model_1.StatType.Sum;
            }
            if (newField.type !== types_1.FieldType.Number) {
                statType = undefined;
            }
            const action = model_1.DatasheetActions.setColumnStatType2Action(snapshot, {
                viewId: view.id,
                fieldId: newField.id,
                statType,
            });
            action && actions.push(action);
        }
        // The grouping field of the kanban board, if the type is converted, 
        // or the multi-selection of the member field is turned on, the grouping field should be clear
        if (view.type === store_1.ViewType.Kanban &&
            view.style.kanbanFieldId === oldField.id &&
            (newField.type !== oldField.type || newField.property.isMulti)) {
            const action = kanban_view_1.KanbanView.setViewStyle2Action(snapshot, {
                viewId: view.id,
                styleKey: constants_1.KanbanStyleKey.KanbanFieldId,
                styleValue: null,
            });
            action && actions.push(action);
        }
        // The Grid view needs to consider the attachment field. 
        // The field that has been set as a grouping item should be deleted after being converted into an attachment field.
        if (view.type === store_1.ViewType.Grid &&
            newField.type !== oldField.type &&
            newField.type === types_1.FieldType.Attachment &&
            view.groupInfo) {
            const groupInfo = view.groupInfo.filter(item => item.fieldId !== newField.id);
            if (groupInfo.length !== view.groupInfo.length) {
                const action = model_1.DatasheetActions.setGroupInfoField2Action(snapshot, {
                    viewId: view.id,
                    groupInfo,
                });
                action && actions.push(action);
            }
        }
    });
    return actions;
}
/**
 * After modifying the field, perform conversion logic on all cell data in this column
 * Make it meet the requirements of the field attribute, and the value of the column cell cannot be out of the limit of the column attribute
 */
function createConvertActions(context, snapshot, oldField, newField) {
    if (oldField.type === newField.type) {
        return {
            actions: changeFieldSetting(snapshot, oldField, newField),
        };
    }
    return switchFieldRecordData(context, snapshot, oldField, newField);
}
exports.createConvertActions = createConvertActions;
function setField(context, snapshot, oldField, newField, datasheetId) {
    const state = context.model;
    const actions = [];
    // When different types are converted to each other, the property needs to be updated
    if (newField.type !== oldField.type) {
        const cellValues = model_1.DatasheetActions.getCellValuesByFieldId(state, snapshot, newField.id);
        const stdVals = cellValues.map(cv => {
            return model_1.Field.bindContext(oldField, state).cellValueToStdValue(cv);
        });
        const property = model_1.Field.bindContext(newField, state).enrichProperty(stdVals);
        newField.property = property;
        // Calculated fields need to determine their own datasheet through the field property, 
        // here we force him to specify the datasheetId of the current command
        if (model_1.Field.bindContext(newField, state).isComputed) {
            newField.property = Object.assign(Object.assign({}, newField.property), { datasheetId });
        }
        if (newField.type === types_1.FieldType.CreatedBy || newField.type === types_1.FieldType.LastModifiedBy) {
            newField.property.uuids = model_1.Field.bindContext(newField, state).getUuidsByRecordMap(snapshot.recordMap);
        }
    }
    const validateFieldPropertyError = model_1.Field.bindContext(newField, state).validateProperty().error;
    if (validateFieldPropertyError) {
        throw new Error(`${(0, i18n_1.t)(i18n_1.Strings.error_set_column_failed_bad_property)}: ${validateFieldPropertyError.details.map(d => d.message).join(',\n')}`);
    }
    // When modifying a field, if the target field of the conversion is a calculated field or an initial non-editable field,
    // For LastModifiedBy/LastModifiedTime field type, fieldIdCollection needs to be updated
    if (!types_1.readonlyFields.has(oldField.type) && types_1.readonlyFields.has(newField.type)) {
        const newActions = setAffectFieldAttr2Action(snapshot, newField.id);
        actions.push(...newActions);
    }
    const setFieldAction = model_1.DatasheetActions.setFieldAttr2Action(snapshot, { field: newField });
    setFieldAction && actions.push(setFieldAction);
    // Convert the value in record to type
    const converted = createConvertActions(context, snapshot, oldField, newField);
    actions.push(...converted.actions);
    /**
     * After the field is converted/deleted, the corresponding functions on the view,
     * such as filtering/grouping, need to be deleted or adjusted synchronously
     */
    actions.push(...clearViewAttribute(snapshot, oldField, newField));
    return {
        actions,
        linkedActions: undefined,
    };
}
exports.setField = setField;
function createNewField(snapshot, field, options) {
    if (!field.property) {
        field.property = (0, model_1.getFieldClass)(field.type).defaultProperty();
    }
    const action = model_1.DatasheetActions.addField2Action(snapshot, {
        viewId: options && options.viewId,
        index: options && options.index,
        fieldId: options && options.fieldId,
        offset: options && options.offset,
        hiddenColumn: options && options.hiddenColumn,
        field,
    });
    if (!action) {
        return [];
    }
    return action;
}
exports.createNewField = createNewField;
function createNewBrotherField(state, newField, datasheetId) {
    const currentDatasheet = (0, selectors_1.getDatasheet)(state, datasheetId);
    // If the new field is the associated table, no action is required
    if (newField.property.foreignDatasheetId === currentDatasheet.id) {
        return {
            datasheetId: '',
            actions: [],
        };
    }
    const foreignSnapshot = (0, selectors_1.getSnapshot)(state, newField.property.foreignDatasheetId);
    const foreignFieldMap = foreignSnapshot.meta.fieldMap;
    const foreignFieldIds = Object.keys(foreignSnapshot.meta.fieldMap);
    const foreignFieldNewId = (0, utils_1.getNewId)(utils_1.IDPrefix.Field, foreignFieldIds);
    newField.property = Object.assign(Object.assign({}, newField.property), { brotherFieldId: foreignFieldNewId });
    /**
     * Create a field that is a sibling field of each other in the associated table.
     */
    const actions = createNewField(foreignSnapshot, {
        id: foreignFieldNewId,
        name: (0, utils_1.getUniqName)(currentDatasheet.name, foreignFieldIds.map(id => foreignFieldMap[id].name)),
        type: types_1.FieldType.Link,
        property: {
            foreignDatasheetId: currentDatasheet.id,
            brotherFieldId: newField.id,
        },
    });
    return {
        datasheetId: newField.property.foreignDatasheetId,
        actions,
    };
}
exports.createNewBrotherField = createNewBrotherField;
function clearOldBrotherField(context, oldField, deleteField) {
    const { model: state } = context;
    // If the old field is not associated with a sibling field, no additional operations are required
    if (!oldField.property.brotherFieldId) {
        return {
            datasheetId: '',
            actions: [],
        };
    }
    const foreignSnapshot = (0, selectors_1.getSnapshot)(state, oldField.property.foreignDatasheetId);
    if (!foreignSnapshot) {
        return null;
    }
    const foreignFieldMap = foreignSnapshot.meta.fieldMap;
    const foreignOldField = foreignFieldMap[oldField.property.brotherFieldId];
    if (!foreignOldField || foreignOldField.property.brotherFieldId !== oldField.id) {
        return null;
    }
    if (deleteField) {
        // delete field
        const actions = model_1.DatasheetActions.deleteField2Action(foreignSnapshot, {
            fieldId: oldField.property.brotherFieldId, datasheetId: state.pageParams.datasheetId
        });
        return actions ? {
            datasheetId: oldField.property.foreignDatasheetId,
            actions,
        } : null;
    }
    /**
     * Convert the sibling link field of the associated table to text type.
     */
    return {
        datasheetId: oldField.property.foreignDatasheetId,
        actions: setField(context, foreignSnapshot, foreignOldField, {
            id: foreignOldField.id,
            name: foreignOldField.name,
            type: types_1.FieldType.Text,
            property: model_1.TextField.defaultProperty(),
        }).actions,
    };
}
exports.clearOldBrotherField = clearOldBrotherField;
/**
 * For some types of fields, it may be necessary to perform corresponding operations when other columns are changed.
 * Temporarily only provides operations required for LastModifiedBy/LastModifiedTime types
 */
function setAffectFieldAttr2Action(snapshot, fieldId) {
    var _a;
    const actions = [];
    const fieldMap = (_a = snapshot.meta) === null || _a === void 0 ? void 0 : _a.fieldMap;
    fieldMap && Object.values(fieldMap).forEach(field => {
        switch (field.type) {
            case types_1.FieldType.LastModifiedBy:
            case types_1.FieldType.LastModifiedTime: {
                const fieldIdCollection = field.property.fieldIdCollection.slice();
                const index = fieldIdCollection.indexOf(fieldId);
                if (index > -1) {
                    fieldIdCollection.splice(index, 1);
                    const newField = Object.assign(Object.assign({}, field), { property: Object.assign(Object.assign({}, field.property), { fieldIdCollection }) });
                    const action = model_1.DatasheetActions.setFieldAttr2Action(snapshot, { field: newField });
                    action && actions.push(action);
                }
                break;
            }
            default:
                break;
        }
    });
    return actions;
}
exports.setAffectFieldAttr2Action = setAffectFieldAttr2Action;
//# sourceMappingURL=field.js.map